import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { carregarProducaoDasTarefas, ehResponsavelDaProducao } from '../utils/responsaveisDaProducao';

// Helper: format number to BRL
const formatBRL = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Shared API call with multiple local fallbacks
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  let lastError = null;
  for (const url of urls) {
    try {
      const config = { method, headers: { 'Content-Type': 'application/json' } };
      const token = getAuthToken();
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      if (data && (method === 'POST' || method === 'PUT')) config.body = JSON.stringify(data);
      const res = await fetch(url, config);
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) {
          try { return await res.json(); } catch { return { success: true, status: res.status }; }
        }
        return await res.text();
      } else {
        const errorText = await res.text().catch(() => `Status ${res.status}`);
        lastError = `${url} - ${errorText}`;
      }
    } catch (e) {
      lastError = `${url} - ${e.message}`;
    }
  }
  throw new Error(lastError || 'Erro de conexão');
};

export default function GestaoFinanceiraDropdown() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // Popup escolher produção
  const [popupProducaoAberto, setPopupProducaoAberto] = useState(false);
  const [producoes, setProducoes] = useState([]);
  const [filtroProducao, setFiltroProducao] = useState('');

  // Produção selecionada
  const [producaoSelecionada, setProducaoSelecionada] = useState(null);

  // Custos calculados
  const [calculatedCosts, setCalculatedCosts] = useState(null);

  // Popup escolher período
  const [popupPeriodoAberto, setPopupPeriodoAberto] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [producoesPeriodo, setProducoesPeriodo] = useState([]);
  const [custosConsolidados, setCustosConsolidados] = useState(null);
  const [todoPeriodoAtivo, setTodoPeriodoAtivo] = useState(false);
  const [periodoConsultado, setPeriodoConsultado] = useState(false);

  // 'producao' = uma produção; 'geral' = soma de todas as produções do período
  const [modo, setModo] = useState('producao');

  // Diferença do Resumo de Custos em "%" ou "x" (multiplicador); lembra a escolha neste navegador
  const [modoDiferenca, setModoDiferenca] = useState(() => {
    try { return localStorage.getItem('gestaoFinanceira.modoDiferenca') === 'x' ? 'x' : '%'; } catch { return '%'; }
  });
  const trocarModoDiferenca = (valor) => {
    setModoDiferenca(valor);
    try { localStorage.setItem('gestaoFinanceira.modoDiferenca', valor); } catch { /* sem armazenamento: vale só nesta tela */ }
  };

  const trocarModo = (novo) => {
    setModo(novo);
    setProducaoSelecionada(null);
    setCalculatedCosts(null);
    setCustosConsolidados(null);
    setProducoesPeriodo([]);
    setPeriodoConsultado(false);
    setTodoPeriodoAtivo(false);
    setDataInicio('');
    setDataFim('');
  };

  // Carregar lista de produções ao abrir popup
  const abrirPopupProducao = async () => {
    setPopupProducaoAberto(true);
    setFiltroProducao('');
    try {
      setLoading(true);
      const data = await apiCall('Producoes');
      setProducoes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar projetos:', e);
      setProducoes([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-selecionar produção passada via URL param ?producaoId=X
  useEffect(() => {
    const pidParam = searchParams.get('producaoId');
    if (!pidParam) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiCall('Producoes');
        const list = Array.isArray(data) ? data : [];
        const found = list.find(p => String(p.ProducaoId || p.producaoId) === String(pidParam));
        if (found) {
          await selecionarProducao(found);
        }
      } catch (e) {
        console.error('Erro ao auto-selecionar projeto:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecionar uma produção e calcular custos
  const selecionarProducao = async (p) => {
    const producaoId = p.ProducaoId || p.producaoId;
    const orcamentoId = p.OrcamentoId || p.orcamentoId;
    setProducaoSelecionada(p);
    setPopupProducaoAberto(false);
    setCustosConsolidados(null);
    setProducoesPeriodo([]);
    setDataInicio('');
    setDataFim('');
    await calcularCustos(producaoId, orcamentoId, p);
  };

  // Matéria-prima perdida/danificada na produção e nas tarefas dela: não foi usada nem voltou ao estoque,
  // mas foi custo da produção — entra no real da matéria-prima
  const custoMateriaPrimaPerdida = async (producaoId) => {
    try {
      const lista = await apiCall(`MateriaPrima/desperdicadas?producaoId=${producaoId}`);
      return (Array.isArray(lista) ? lista : []).reduce((sum, d) => sum + (Number(d.valor ?? d.Valor ?? 0) || 0), 0);
    } catch (e) {
      console.warn('Erro MateriaPrima perdida:', e);
      return 0;
    }
  };

  // Matéria-prima prevista no orçamento: vem do histórico (MateriaPrimaOrcamento), que guarda o previsto
  // mesmo depois que a produção gasta a reserva. Sem histórico, usa as reservas do orçamento.
  const materiaPrimaPrevista = async (orcamentoId) => {
    const somar = (lista, campo) => (Array.isArray(lista) ? lista : [])
      .reduce((sum, m) => sum + (Number(m[campo] ?? m[campo.charAt(0).toUpperCase() + campo.slice(1)] ?? 0) || 0), 0);
    try {
      const previsto = await apiCall(`MateriaPrima/previsto-orcamento/${orcamentoId}`);
      if (Array.isArray(previsto) && previsto.length > 0) return somar(previsto, 'valorTotal');
    } catch (e) { console.warn('Erro previsto do orçamento:', e); }
    const reservas = await apiCall(`MateriaPrima/por-orcamento/${orcamentoId}`);
    return somar(reservas, 'valor');
  };

  // Calcular custos Previsto vs Real para uma produção
  const calcularCustos = async (producaoId, orcamentoId, p) => {
    try {
      setLoading(true);

      // === CUSTO REAL (da produção) ===
      let laborReal = 0, materiaReal = 0, servicosTerceirosReal = 0;

      // Responsáveis reais
      try {
        const respData = await apiCall('Responsaveis');
        const respArr = Array.isArray(respData) ? respData : [];
        const producaoDaTarefa = await carregarProducaoDasTarefas(apiCall);
        const vinculados = respArr.filter(r => ehResponsavelDaProducao(r, producaoId, producaoDaTarefa));
        const enriched = vinculados.map(r => ({
          ...r,
          custoHora: Number(r.pessoaCustoHora || r.PessoaCustoHora || r.custoHora || r.CustoHora || 0),
          horasReais: Number(r.horasReais || r.HorasReais || 0)
        }));
        laborReal = enriched.reduce((s, r) => s + (r.horasReais * r.custoHora), 0);
      } catch (e) { console.warn('Erro Responsáveis real:', e); }

      // Matéria-prima real
      try {
        const mpList = await apiCall(`MateriaPrima/por-producao/${producaoId}`);
        const arrMP = Array.isArray(mpList) ? mpList : [];
        materiaReal = arrMP.reduce((sum, m) => {
          return sum + (Number(m.Valor ?? m.valor ?? 0) || 0);
        }, 0);
        materiaReal += await custoMateriaPrimaPerdida(producaoId);
      } catch (e) { console.warn('Erro MateriaPrima real:', e); }

      // Serviços terceiros reais
      try {
        const servAll = await apiCall('ServicosTerceiros');
        const svcArr = Array.isArray(servAll) ? servAll : [];
        const matched = svcArr.filter(s => (s.producaoId || s.ProducaoId) == producaoId && (s.orcamentoId ?? s.OrcamentoId) == null); // real: do projeto, sem orçamento
        servicosTerceirosReal = matched.reduce((sum, s) => sum + (Number(s.valor ?? s.Valor ?? 0) || 0), 0);
      } catch (e) { console.warn('Erro ServicosTerceiros real:', e); }

      const totalReal = laborReal + materiaReal + servicosTerceirosReal;

      // === CUSTO PREVISTO (do orçamento) ===
      let orcamentoLaborPrevisto = 0, orcamentoMateriaPrevista = 0, orcamentoServicosTerceiros = 0, orcamentoValorTotal = 0;

      if (orcamentoId) {
        try {
          const orcData = await apiCall(`Orcamento/${orcamentoId}`);
          orcamentoValorTotal = Number(orcData.preco_final ?? orcData.Preco_final ?? orcData.valor ?? orcData.Valor ?? 0) || 0;

          const respOrc = await apiCall(`Responsaveis/por-orcamento/${orcamentoId}`);
          const arrRespOrc = Array.isArray(respOrc) ? respOrc : [];
          orcamentoLaborPrevisto = arrRespOrc.reduce((s, r) => {
            const hPrev = Number(r.horasPrevistas || r.HorasPrevistas || 0) || 0;
            const custo = Number(r.pessoaCustoHora || r.PessoaCustoHora || r.custoHora || r.CustoHora || 0) || 0;
            return s + (hPrev * custo);
          }, 0);

          orcamentoMateriaPrevista = await materiaPrimaPrevista(orcamentoId);
        } catch (e) { console.warn('Erro dados orçamento previsto:', e); }

        try {
          const stOrc = await apiCall(`ServicosTerceiros/orcamento/${orcamentoId}`);
          const arrStOrc = Array.isArray(stOrc) ? stOrc : [];
          orcamentoServicosTerceiros = arrStOrc.filter(s => (s.producaoId ?? s.ProducaoId) == null).reduce((sum, s) => sum + (Number(s.valor ?? s.Valor ?? 0) || 0), 0);
        } catch (e) { console.warn('Erro ServicosTerceiros orçamento:', e); }
      }

      const totalPrevisto = orcamentoLaborPrevisto + orcamentoMateriaPrevista + orcamentoServicosTerceiros;

      setCalculatedCosts({
        orcamentoLaborPrevisto, orcamentoMateriaPrevista, orcamentoServicosTerceiros, orcamentoValorTotal, totalPrevisto,
        laborReal, materiaReal, servicosTerceirosReal, totalReal,
        valorContrato: Number(p.ValorContrato || p.valorContrato || 0) || 0
      });
    } catch (e) {
      console.error('Erro ao calcular custos:', e);
    } finally {
      setLoading(false);
    }
  };

  // Data da produção: início dela (preenchido quando o contrato é assinado); sem isso, assinatura/criação
  // do contrato e, por último, o prazo de entrega. Aceita "2026-10-25", "2026-10-25T10:00" e "25/10/2026".
  const dataDaProducao = (p) => {
    const valores = [
      p.DataInicio ?? p.dataInicio,
      p.Data_assinaturaContrato ?? p.data_assinaturaContrato,
      p.DataCriacaoContrato ?? p.dataCriacaoContrato,
      p.PrazoEntrega ?? p.prazoEntrega,
      p.DataEntrega ?? p.dataEntrega
    ];
    for (const v of valores) {
      if (!v) continue;
      const texto = String(v).trim();
      const br = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const d = br ? new Date(+br[3], +br[2] - 1, +br[1]) : iso ? new Date(+iso[1], +iso[2] - 1, +iso[3]) : null;
      if (d && !isNaN(d)) return d;
    }
    return null;
  };

  // Data local no formato do input date (toISOString usa UTC e podia pular um dia)
  const paraInputDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const aplicarPeriodoRapido = (tipo) => {
    if (tipo === 'todo') {
      setDataInicio('');
      setDataFim('');
      calcularPorPeriodo('', '', true);
      return;
    }
    const hoje = new Date();
    const inicio = new Date(hoje);
    if (tipo === 'ano') inicio.setFullYear(inicio.getFullYear() - 1);
    else inicio.setMonth(inicio.getMonth() - 1);
    const ini = paraInputDate(inicio);
    const fim = paraInputDate(hoje);
    setDataInicio(ini);
    setDataFim(fim);
    calcularPorPeriodo(ini, fim);
  };

  // Gestão geral: soma todas as produções do período (ou de todo o período)
  const calcularPorPeriodo = async (inicioVal = dataInicio, fimVal = dataFim, todoPeriodo = false) => {
    if (!todoPeriodo && (!inicioVal || !fimVal)) return;
    setProducaoSelecionada(null);
    setCalculatedCosts(null);
    setTodoPeriodoAtivo(todoPeriodo);
    setPeriodoConsultado(true);

    try {
      setLoading(true);
      const data = await apiCall('Producoes');
      const arr = Array.isArray(data) ? data : [];

      const [ai, mi, di] = String(inicioVal).split('-').map(Number);
      const [af, mf, df] = String(fimVal).split('-').map(Number);
      const inicio = todoPeriodo ? null : new Date(ai, mi - 1, di);
      const fim = todoPeriodo ? null : new Date(af, mf - 1, df, 23, 59, 59, 999);

      const filtradas = todoPeriodo ? arr : arr.filter(p => {
        const d = dataDaProducao(p);
        return d && d >= inicio && d <= fim;
      });

      setProducoesPeriodo(filtradas);

      if (filtradas.length === 0) {
        setCustosConsolidados(null);
        return;
      }

      // Acumular custos de todas as produções no período
      let totalLaborReal = 0, totalMateriaReal = 0, totalSTReal = 0;
      let totalLaborPrev = 0, totalMateriaPrev = 0, totalSTPrev = 0;
      let totalOrcValor = 0;

      // Carregar responsáveis e ST uma vez
      let allResp = [], allST = [], producaoDaTarefa = {};
      try {
        const respData = await apiCall('Responsaveis');
        allResp = Array.isArray(respData) ? respData : [];
        producaoDaTarefa = await carregarProducaoDasTarefas(apiCall);
      } catch (e) { console.warn(e); }
      try {
        const stData = await apiCall('ServicosTerceiros');
        allST = Array.isArray(stData) ? stData : [];
      } catch (e) { console.warn(e); }

      for (const p of filtradas) {
        const pid = p.ProducaoId || p.producaoId;
        const oid = p.OrcamentoId || p.orcamentoId;

        // Real - Responsáveis
        const respProd = allResp.filter(r => ehResponsavelDaProducao(r, pid, producaoDaTarefa));
        totalLaborReal += respProd.reduce((s, r) => {
          const hrs = Number(r.horasReais || r.HorasReais || 0);
          const custo = Number(r.pessoaCustoHora || r.PessoaCustoHora || r.custoHora || r.CustoHora || 0);
          return s + (hrs * custo);
        }, 0);

        // Real - Matéria-prima
        try {
          const mpList = await apiCall(`MateriaPrima/por-producao/${pid}`);
          const arrMP = Array.isArray(mpList) ? mpList : [];
          totalMateriaReal += arrMP.reduce((sum, m) => {
            return sum + (Number(m.Valor ?? m.valor ?? 0) || 0);
          }, 0);
          totalMateriaReal += await custoMateriaPrimaPerdida(pid);
        } catch (e) { /* skip */ }

        // Real - ST
        const stProd = allST.filter(s => (s.producaoId || s.ProducaoId) == pid && (s.orcamentoId ?? s.OrcamentoId) == null); // real: do projeto, sem orçamento
        totalSTReal += stProd.reduce((sum, s) => sum + (Number(s.valor ?? s.Valor ?? 0) || 0), 0);

        // Previsto (do orçamento)
        if (oid) {
          try {
            const orcData = await apiCall(`Orcamento/${oid}`);
            totalOrcValor += Number(orcData.preco_final ?? orcData.Preco_final ?? orcData.valor ?? orcData.Valor ?? 0) || 0;

            const respOrc = await apiCall(`Responsaveis/por-orcamento/${oid}`);
            const arrRespOrc = Array.isArray(respOrc) ? respOrc : [];
            totalLaborPrev += arrRespOrc.reduce((s, r) => {
              return s + ((Number(r.horasPrevistas || r.HorasPrevistas || 0) || 0) * (Number(r.pessoaCustoHora || r.PessoaCustoHora || r.custoHora || r.CustoHora || 0) || 0));
            }, 0);

            totalMateriaPrev += await materiaPrimaPrevista(oid);
          } catch (e) { /* skip */ }

          try {
            const stOrc = await apiCall(`ServicosTerceiros/orcamento/${oid}`);
            const arrStOrc = Array.isArray(stOrc) ? stOrc : [];
            totalSTPrev += arrStOrc.filter(s => (s.producaoId ?? s.ProducaoId) == null).reduce((sum, s) => sum + (Number(s.valor ?? s.Valor ?? 0) || 0), 0);
          } catch (e) { /* skip */ }
        }
      }

      const totalPrevisto = totalLaborPrev + totalMateriaPrev + totalSTPrev;
      const totalReal = totalLaborReal + totalMateriaReal + totalSTReal;

      setCustosConsolidados({
        orcamentoLaborPrevisto: totalLaborPrev, orcamentoMateriaPrevista: totalMateriaPrev, orcamentoServicosTerceiros: totalSTPrev, orcamentoValorTotal: totalOrcValor, totalPrevisto,
        laborReal: totalLaborReal, materiaReal: totalMateriaReal, servicosTerceirosReal: totalSTReal, totalReal,
        valorContrato: filtradas.reduce((sum, p) => sum + (Number(p.ValorContrato || p.valorContrato || 0) || 0), 0)
      });
    } catch (e) {
      console.error('Erro ao calcular por período:', e);
    } finally {
      setLoading(false);
    }
  };

  // Renderizar dashboard de custos (reutilizado para produção individual e período)
  const renderDashboardCustos = (costs) => {
    if (!costs) return null;

    // "Ver mais" abre o detalhe em nova aba; com uma produção escolhida, já abre o dela
    const pid = modo === 'producao' && producaoSelecionada ? (producaoSelecionada.ProducaoId || producaoSelecionada.producaoId) : null;
    const items = [
      { label: 'Responsáveis', abbr: 'RS', previsto: costs.orcamentoLaborPrevisto || 0, real: costs.laborReal || 0,
        verMais: `/sidebar/ResumoResponsaveisDropdown${pid ? `?producaoId=${pid}` : ''}` },
      { label: 'Matéria-Prima', abbr: 'MP', previsto: costs.orcamentoMateriaPrevista || 0, real: costs.materiaReal || 0,
        verMais: `/sidebar/MateriaPrimaDropdown${pid ? `?filtroCampo=producao&filtroValor=${pid}` : ''}` },
      { label: 'Serv. Terceiros', abbr: 'ST', previsto: costs.orcamentoServicosTerceiros || 0, real: costs.servicosTerceirosReal || 0,
        verMais: `/sidebar/ResumoServicosTerceirosDropdown${pid ? `?producaoId=${pid}` : ''}` },
    ];
    const colunas = '1.5fr 1fr 1fr 1fr 0.9fr';
    const totais = { previsto: costs.totalPrevisto || 0, real: costs.totalReal || 0 };

    // Diferença do real em relação ao previsto, no formato escolhido na chave: "%" ou "x" (multiplicador)
    const getDiff = (prev, real) => {
      if (modoDiferenca === 'x') {
        // x = real ÷ previsto. Previsto zero: 0x sem gasto; com gasto, 2x (o mesmo que +100%)
        if (prev === 0) return real === 0 ? { text: '0x', color: '#4caf50' } : { text: '2x', color: '#d32f2f' };
        const ratio = real / prev;
        const texto = ratio >= 1 ? ratio.toFixed(1) : ratio.toFixed(2);
        return { text: `${texto.replace(/\.?0+$/, '') || '0'}x`, color: ratio > 1 ? '#d32f2f' : '#4caf50' };
      }
      if (prev === 0 && real === 0) return { text: '0%', color: '#4caf50' };
      // Previsto zero e real acima de zero: não dá para comparar em %, então é sempre +100% (gastou tudo além do previsto)
      if (prev === 0) return { text: '+100%', color: '#d32f2f' };
      const pct = ((real - prev) / prev) * 100;
      const sign = pct > 0 ? '+' : '';
      return { text: `${sign}${pct.toFixed(1)}%`, color: pct > 0 ? '#d32f2f' : '#4caf50' };
    };

    // Chave de dois lados (x | %): o lado escolhido fica laranja
    // Um botão só: clicar em qualquer lugar dele troca entre x e %
    const chaveDiferenca = (
      <button
        type="button"
        onClick={() => trocarModoDiferenca(modoDiferenca === 'x' ? '%' : 'x')}
        aria-label={`Diferença em ${modoDiferenca === 'x' ? 'x' : '%'}: clique para trocar`}
        title="Clique para trocar entre multiplicador (x) e porcentagem (%)"
        style={{ display: 'inline-flex', alignSelf: 'flex-end', marginTop: 4, marginRight: 0, padding: 0, border: '1px solid rgb(235, 133, 0)', borderRadius: 6, overflow: 'hidden', background: '#fff', cursor: 'pointer' }}
      >
        {['x', '%'].map(valor => {
          const ativo = modoDiferenca === valor;
          return (
            <span
              key={valor}
              style={{ padding: '2px 12px', fontSize: 12, fontWeight: 700, background: ativo ? 'rgb(235, 133, 0)' : '#fff', color: ativo ? '#fff' : 'rgb(235, 133, 0)' }}
            >
              {valor}
            </span>
          );
        })}
      </button>
    );

    const FIXED_BAR = 80;
    const getBarH = (prev, real) => {
      if (prev === 0 && real === 0) return 0;
      if (prev === 0) return FIXED_BAR;
      const ratio = Math.min(real / prev, 2);
      return Math.max(ratio * FIXED_BAR, 4);
    };

    return (
      <div style={{ marginTop: 18, padding: 16, background: '#fff8e1', borderRadius: 8, border: '1px solid #ffecb3' }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#b26a00' }}>📊 Resumo de Custos</h4>

        {/* Tabela */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: colunas, alignItems: 'center', padding: '10px 14px', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', fontWeight: 700, fontSize: 12, color: '#555' }}>
            <div>Item</div>
            <div style={{ textAlign: 'right' }}>Previsto</div>
            <div style={{ textAlign: 'right' }}>Real</div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              Diferença
              {chaveDiferenca}
            </div>
            <div style={{ textAlign: 'center' }}>Detalhes</div>
          </div>
          {items.map((it, i) => {
            const diff = getDiff(it.previsto, it.real);
            return (
              <div key={it.abbr} style={{ display: 'grid', gridTemplateColumns: colunas, alignItems: 'center', padding: '10px 14px', borderBottom: i < items.length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: '#333' }}>{it.label} <span style={{ color: '#999', fontWeight: 400 }}>({it.abbr})</span></div>
                <div style={{ textAlign: 'right', color: '#1e88e5', fontWeight: 600 }}>{formatBRL(it.previsto)}</div>
                <div style={{ textAlign: 'right', color: '#e53935', fontWeight: 600 }}>{formatBRL(it.real)}</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: diff.color }}>({diff.text})</div>
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn-laranja-st"
                    onClick={() => window.open(it.verMais, '_blank', 'noopener')}
                    title={`Abrir o detalhe de ${it.label} em uma nova aba`}
                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}
                  >
                    Ver mais
                  </button>
                </div>
              </div>
            );
          })}
          <div style={{ display: 'grid', gridTemplateColumns: colunas, alignItems: 'center', padding: '12px 14px', background: '#f9f9f9', borderTop: '2px solid #e0e0e0', fontSize: 14 }}>
            <div style={{ fontWeight: 700, color: '#333' }}>TOTAL</div>
            <div style={{ textAlign: 'right', color: '#1e88e5', fontWeight: 700 }}>{formatBRL(totais.previsto)}</div>
            <div style={{ textAlign: 'right', color: '#e53935', fontWeight: 700 }}>{formatBRL(totais.real)}</div>
            <div style={{ textAlign: 'right', fontWeight: 700, color: getDiff(totais.previsto, totais.real).color }}>({getDiff(totais.previsto, totais.real).text})</div>
            <div></div>
          </div>
        </div>

        {/* Gráfico de Barras */}
        <div style={{ marginTop: 20, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', paddingBottom: 8 }}>
            {items.map(it => {
              const diff = getDiff(it.previsto, it.real);
              const barH = getBarH(it.previsto, it.real);
              return (
                <div key={it.abbr} style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: diff.color, marginBottom: 4, minHeight: 16 }}>{diff.text}</div>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'flex-end', height: FIXED_BAR * 2 + 10 }}>
                    <div style={{ width: 26, height: it.previsto > 0 ? FIXED_BAR : 0, background: '#1e88e5', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} title={`Previsto: ${formatBRL(it.previsto)}`} />
                    <div style={{ width: 26, height: barH, background: '#e53935', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} title={`Real: ${formatBRL(it.real)}`} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6, color: '#333' }}>{it.abbr}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{it.label}</div>
                </div>
              );
            })}
          </div>

          {/* Legenda */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 12, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <div style={{ width: 12, height: 12, background: '#1e88e5', borderRadius: 2 }} /> <span style={{ color: '#666' }}>Previsto</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <div style={{ width: 12, height: 12, background: '#e53935', borderRadius: 2 }} /> <span style={{ color: '#666' }}>Real</span>
            </div>
          </div>

          <hr style={{ margin: '0 0 16px 0', border: 'none', borderTop: '1px dashed #ccc' }} />

          {/* Barra TOTAL */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: getDiff(totais.previsto, totais.real).color, marginBottom: 4 }}>
              {getDiff(totais.previsto, totais.real).text}
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'flex-end', height: FIXED_BAR * 2 + 10 }}>
              <div style={{ width: 36, height: totais.previsto > 0 ? FIXED_BAR : 0, background: '#1e88e5', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} title={`Previsto: ${formatBRL(totais.previsto)}`} />
              <div style={{ width: 36, height: getBarH(totais.previsto, totais.real), background: '#e53935', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} title={`Real: ${formatBRL(totais.real)}`} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, marginTop: 6, color: '#333' }}>TOTAL</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 12, marginTop: 4 }}>
              <span style={{ color: '#1e88e5', fontWeight: 600 }}>Prev: {formatBRL(totais.previsto)}</span>
              <span style={{ color: '#e53935', fontWeight: 600 }}>Real: {formatBRL(totais.real)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar análise de lucro
  const renderAnaliseLucro = (costs) => {
    if (!costs) return null;

    const custosPrevistosDetalhados = (costs.orcamentoLaborPrevisto || 0) + (costs.orcamentoMateriaPrevista || 0) + (costs.orcamentoServicosTerceiros || 0);
    const valorContrato = costs.valorContrato || 0;
    const orcamentoValorTotal = costs.orcamentoValorTotal || costs.totalPrevisto || 0;
    const totalReal = costs.totalReal || 0;

    // Lucro previsto = preço final do orçamento - custos previstos
    const lucroPrevisto = orcamentoValorTotal - custosPrevistosDetalhados;
    // Lucro real = valor do contrato - custos reais da produção
    const lucroReal = valorContrato - totalReal;

    // Porcentagem do lucro real em relação ao previsto
    let porcentagem = 0;
    if (lucroPrevisto > 0) {
      porcentagem = (lucroReal / lucroPrevisto) * 100;
    } else if (lucroPrevisto === 0 && lucroReal > 0) {
      porcentagem = 999; // lucrou sem previsão
    } else if (lucroPrevisto === 0 && lucroReal <= 0) {
      porcentagem = 0;
    } else {
      // lucroPrevisto negativo (prejuízo previsto)
      porcentagem = 0;
    }

    // Determinar faixa de cor e texto
    let corFaixa, bgFaixa, textoAnalise;

    if (porcentagem > 100) {
      corFaixa = '#1b5e20';
      bgFaixa = '#e8f5e9';
      textoAnalise = `Parabéns! Você previa lucrar ${formatBRL(lucroPrevisto)} com este projeto e acabou lucrando ${formatBRL(lucroReal)}, superando a expectativa de lucro do orçamento. O lucro real foi maior do que o previsto.`;
    } else if (porcentagem >= 80) {
      corFaixa = '#2e7d32';
      bgFaixa = '#e8f5e9';
      textoAnalise = `Você previa lucrar ${formatBRL(lucroPrevisto)} com este projeto e acabou lucrando ${formatBRL(lucroReal)}. Houve uma redução de ${(100 - porcentagem).toFixed(1)}%, mas o lucro ainda está dentro da margem prevista no orçamento.`;
    } else if (porcentagem >= 60) {
      corFaixa = '#f9a825';
      bgFaixa = '#fffde7';
      textoAnalise = `Você previa lucrar ${formatBRL(lucroPrevisto)} com este projeto e acabou lucrando ${formatBRL(lucroReal)}, que representa apenas ${porcentagem.toFixed(1)}% do lucro previsto. O lucro real ficou abaixo do planejado inicialmente.`;
    } else if (porcentagem > 0) {
      corFaixa = '#c62828';
      bgFaixa = '#ffebee';
      textoAnalise = `Você previa lucrar ${formatBRL(lucroPrevisto)} com este projeto, mas acabou lucrando apenas ${formatBRL(lucroReal)}, que é apenas ${porcentagem.toFixed(1)}% do lucro previsto. A margem ficou muito abaixo dos 60% esperados do lucro planejado.`;
    } else if (porcentagem === 0 && lucroReal === 0) {
      corFaixa = '#c62828';
      bgFaixa = '#ffebee';
      textoAnalise = `Você previa lucrar ${formatBRL(lucroPrevisto)} com este projeto, porém não obteve nenhum lucro. Os custos reais do projeto consumiram todo o valor recebido pelo contrato, resultando em lucro zero.`;
    } else {
      // Negativo = prejuízo
      corFaixa = '#b71c1c';
      bgFaixa = '#ffcdd2';
      textoAnalise = `Você previa lucrar ${formatBRL(lucroPrevisto)} com este projeto, mas acabou tendo um prejuízo de ${formatBRL(Math.abs(lucroReal))}. Os custos reais superaram o valor recebido pelo contrato, gerando um resultado negativo neste projeto.`;
    }

    const FIXED_BAR = 80;
    const maxVal = Math.max(Math.abs(lucroPrevisto), Math.abs(lucroReal), 1);
    const barPrev = lucroPrevisto > 0 ? (lucroPrevisto / maxVal) * FIXED_BAR : 0;
    const barReal = lucroReal > 0 ? (lucroReal / maxVal) * FIXED_BAR : 0;

    return (
      <div style={{ marginTop: 18, padding: 16, background: bgFaixa, borderRadius: 8, border: `1px solid ${corFaixa}33` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h4 style={{ margin: 0, color: corFaixa }}>💰 Análise de Lucro</h4>
          <span style={{
            padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            border: `2px solid ${corFaixa}`, color: corFaixa, background: 'transparent'
          }}>
            {porcentagem > 100 ? 'Acima da margem' : porcentagem >= 80 ? 'Dentro da margem' : porcentagem >= 60 ? 'Margem reduzida' : porcentagem > 0 ? 'Fora da margem' : lucroReal === 0 ? 'Sem lucro' : 'Prejuízo'}
          </span>
        </div>

        {/* Container Lucro Real */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ padding: '10px 14px', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', fontWeight: 700, fontSize: 13, color: '#2e7d32' }}>Lucro Real</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#333' }}>Valor do Contrato</div>
            <div style={{ textAlign: 'right', fontWeight: 600, color: '#1565c0' }}>{formatBRL(valorContrato)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#333' }}>Custos Reais (Projeto)</div>
            <div style={{ textAlign: 'right', fontWeight: 600, color: '#e53935' }}>- {formatBRL(totalReal)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '12px 14px', background: '#f9f9f9', borderTop: '2px solid #e0e0e0', fontSize: 14 }}>
            <div style={{ fontWeight: 700, color: '#333' }}>Lucro Real</div>
            <div style={{ textAlign: 'right', fontWeight: 700, color: lucroReal >= 0 ? '#2e7d32' : '#c62828' }}>{formatBRL(lucroReal)}</div>
          </div>
        </div>

        {/* Container Lucro Previsto */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '10px 14px', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', fontWeight: 700, fontSize: 13, color: '#e53935' }}>Lucro Previsto</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#333' }}>Valor do Orçamento</div>
            <div style={{ textAlign: 'right', fontWeight: 600, color: '#1565c0' }}>{formatBRL(orcamentoValorTotal)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#333' }}>Custos Previstos (Orçados)</div>
            <div style={{ textAlign: 'right', fontWeight: 600, color: '#e53935' }}>- {formatBRL(custosPrevistosDetalhados)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '12px 14px', background: '#f9f9f9', borderTop: '2px solid #e0e0e0', fontSize: 14 }}>
            <div style={{ fontWeight: 700, color: '#333' }}>Lucro Previsto</div>
            <div style={{ textAlign: 'right', fontWeight: 700, color: lucroPrevisto >= 0 ? '#e53935' : '#c62828' }}>{formatBRL(lucroPrevisto)}</div>
          </div>
        </div>

        {/* Gráfico de barras Lucro Previsto vs Real */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 40, paddingBottom: 8 }}>
            {/* Barra Lucro Previsto */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e53935', marginBottom: 6 }}>{formatBRL(lucroPrevisto)}</div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: FIXED_BAR * 2 + 10 }}>
                <div style={{ width: 40, height: barPrev > 0 ? barPrev : 4, background: '#e53935', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6, color: '#333' }}>Previsto</div>
            </div>

            {/* Porcentagem central */}
            <div style={{ textAlign: 'center', marginBottom: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
              <div style={{
                fontSize: 22, fontWeight: 800,
                color: porcentagem > 100 ? '#1b5e20' : porcentagem >= 80 ? '#2e7d32' : porcentagem >= 60 ? '#9e9e9e' : '#c62828',
                lineHeight: 1
              }}>
                {lucroPrevisto > 0 ? `${porcentagem.toFixed(1)}%` : '—'}
              </div>
              <div style={{ fontSize: 10, color: '#999', marginTop: 3, fontWeight: 600 }}>Real / Previsto</div>
            </div>

            {/* Barra Lucro Real */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: lucroReal >= 0 ? '#2e7d32' : '#c62828', marginBottom: 6 }}>{formatBRL(lucroReal)}</div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: FIXED_BAR * 2 + 10 }}>
                <div style={{ width: 40, height: barReal > 0 ? barReal : 4, background: lucroReal >= 0 ? '#2e7d32' : '#c62828', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6, color: '#333' }}>Real</div>
            </div>
          </div>

          {/* Legenda */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <div style={{ width: 12, height: 12, background: '#e53935', borderRadius: 2 }} /> <span style={{ color: '#666' }}>Lucro Previsto</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <div style={{ width: 12, height: 12, background: '#2e7d32', borderRadius: 2 }} /> <span style={{ color: '#666' }}>Lucro Real</span>
            </div>
          </div>
        </div>

        {/* Container Lucro Previsto */}
        <div style={{ padding: 14, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14, lineHeight: 1.6, color: '#333', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: '#e53935', marginBottom: 6, fontSize: 13 }}>📋 Lucro Previsto</div>
          <div>O valor final do orçamento era de <strong>{formatBRL(orcamentoValorTotal)}</strong>. Os custos previstos no orçamento (responsáveis, matéria-prima e serviços terceiros) totalizam <strong>{formatBRL(custosPrevistosDetalhados)}</strong>. Portanto, o lucro previsto para este projeto era de <strong style={{ color: '#e53935' }}>{formatBRL(lucroPrevisto)}</strong>.</div>
        </div>

        {/* Container Lucro Real */}
        <div style={{ padding: 14, background: '#fff', borderRadius: 8, border: `2px solid ${corFaixa}44`, fontSize: 14, lineHeight: 1.6, color: '#333', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: corFaixa, marginBottom: 6, fontSize: 13 }}>
            {porcentagem > 100 ? '✅ Lucro Real — Acima do previsto' : porcentagem >= 80 ? '✅ Lucro Real — Dentro da margem' : porcentagem >= 60 ? '⚠️ Lucro Real — Abaixo do ideal' : porcentagem > 0 ? '🚨 Lucro Real — Muito abaixo do previsto' : lucroReal === 0 ? '🚨 Lucro Real — Zero' : '❌ Lucro Real — Prejuízo'}
          </div>
          <div>O valor do contrato foi de <strong>{formatBRL(valorContrato)}</strong>. Os custos reais do projeto (responsáveis, matéria-prima e serviços terceiros) totalizam <strong>{formatBRL(totalReal)}</strong>. O lucro real obtido foi de <strong style={{ color: lucroReal >= 0 ? '#2e7d32' : '#c62828' }}>{formatBRL(lucroReal)}</strong>.</div>
          <div style={{ marginTop: 8, padding: 10, background: bgFaixa, borderRadius: 6, border: `1px solid ${corFaixa}33` }}>
            {textoAnalise}
          </div>
        </div>
      </div>
    );
  };

  // Filtrar produções no popup
  const producoesFiltradas = producoes.filter(p => {
    const val = filtroProducao.toLowerCase();
    if (!val) return true;
    const id = String(p.ProducaoId || p.producaoId || '');
    const titulo = (p.Titulo || p.titulo || '').toLowerCase();
    const cliente = (p.ClienteNome || p.clienteNome || '').toLowerCase();
    return id.includes(val) || titulo.includes(val) || cliente.includes(val);
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#fff', zIndex: 1000, display: 'flex', flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {loading && <LoadingOverlay />}

      {/* Header */}
      <div style={{
        padding: '16px 24px', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 16,
        background: '#fafafa', flexShrink: 0
      }}>
        <button
          onClick={() => window.history.back()}
          style={{
            padding: '8px 20px', background: '#f44336', color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14
          }}
        >
          ← Voltar
        </button>
        <h2 style={{ margin: 0, color: '#333', fontSize: 20 }}>💰 Gestão Financeira</h2>
      </div>

      {/* Modo (um projeto ou gestão geral) + controles do modo */}
      <div style={{ padding: '10px 24px 6px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select
          value={modo}
          onChange={e => trocarModo(e.target.value)}
          style={{
            padding: '7px 12px', background: '#fff', color: '#333', border: '2px solid rgb(235, 133, 0)',
            borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13
          }}
        >
          <option value="producao">Por projeto</option>
          <option value="geral">Gestão geral (todos os projetos)</option>
        </select>

        <div style={{ width: 1, height: 24, background: '#ddd', margin: '0 4px' }} />

        {modo === 'producao' ? (
          <button
            onClick={abrirPopupProducao}
            style={{
              padding: '7px 18px', background: 'rgb(235, 133, 0)', color: '#fff', border: 'none',
              borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
            }}
          >
            Escolher Projeto
          </button>
        ) : (
          <>
            <button onClick={() => aplicarPeriodoRapido('ano')}
              style={{ padding: '7px 14px', background: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              Último Ano
            </button>
            <button onClick={() => aplicarPeriodoRapido('mes')}
              style={{ padding: '7px 14px', background: '#1e88e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              Último Mês
            </button>
            <button onClick={() => aplicarPeriodoRapido('todo')}
              style={{ padding: '7px 14px', background: '#455a64', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              Todo o Período
            </button>

            <div style={{ width: 1, height: 24, background: '#ddd', margin: '0 4px' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>Escolher período:</span>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
            <span style={{ color: '#888', fontWeight: 600, fontSize: 13 }}>—</span>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
            <button onClick={() => calcularPorPeriodo(dataInicio, dataFim)} disabled={!dataInicio || !dataFim}
              style={{
                padding: '7px 16px', background: (!dataInicio || !dataFim) ? '#ccc' : '#1565c0', color: '#fff',
                border: 'none', borderRadius: 6, cursor: (!dataInicio || !dataFim) ? 'default' : 'pointer', fontWeight: 600, fontSize: 13
              }}>
              Filtrar
            </button>
          </>
        )}
      </div>

      {/* Conteúdo principal */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px' }}>

        {/* Indicador de projeto selecionado */}
        {producaoSelecionada && (
          <div style={{ padding: 14, background: '#e3f2fd', borderRadius: 8, border: '1px solid #90caf9', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1565c0' }}>
              Projeto #{producaoSelecionada.ProducaoId || producaoSelecionada.producaoId} — {producaoSelecionada.Titulo || producaoSelecionada.titulo || 'Sem título'}
            </div>
            <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
              Cliente: {producaoSelecionada.ClienteNome || producaoSelecionada.clienteNome || '—'} | Orçamento: {formatBRL(producaoSelecionada.OrcamentoValor || producaoSelecionada.orcamentoValor)}
            </div>
          </div>
        )}

        {/* Indicador de período */}
        {modo === 'geral' && periodoConsultado && !loading && (
          <div style={{ padding: 14, background: producoesPeriodo.length > 0 ? '#e8f5e9' : '#fff8e1', borderRadius: 8, border: `1px solid ${producoesPeriodo.length > 0 ? '#a5d6a7' : '#ffe082'}`, marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: producoesPeriodo.length > 0 ? '#2e7d32' : '#b26a00' }}>
              Gestão geral — {todoPeriodoAtivo
                ? 'todo o período'
                : `${dataInicio.split('-').reverse().join('/')} a ${dataFim.split('-').reverse().join('/')}`}
            </div>
            <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
              {producoesPeriodo.length > 0
                ? `${producoesPeriodo.length} projeto(ões) somada(s) — pela data de início do projeto`
                : 'Nenhum projeto iniciado neste período.'}
            </div>
          </div>
        )}

        {/* Análise de lucro — projeto individual */}
        {producaoSelecionada && calculatedCosts && renderAnaliseLucro(calculatedCosts)}
        {/* Resumo de custos — projeto individual */}
        {producaoSelecionada && calculatedCosts && renderDashboardCustos(calculatedCosts)}

        {/* Análise de lucro — período */}
        {custosConsolidados && renderAnaliseLucro(custosConsolidados)}
        {/* Resumo de custos — período */}
        {custosConsolidados && renderDashboardCustos(custosConsolidados)}

        {/* Estado vazio */}
        {!producaoSelecionada && !custosConsolidados && !periodoConsultado && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Gestão Financeira</div>
            <div style={{ fontSize: 14 }}>
              {modo === 'producao'
                ? 'Clique em "Escolher Projeto" para ver o resumo de um projeto.'
                : 'Escolha Último Ano, Último Mês, Todo o Período ou um período para somar todos os projetos.'}
            </div>
          </div>
        )}
      </div>

      {/* POPUP: Escolher Projeto */}
      {popupProducaoAberto && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, width: '90%', maxWidth: 700, maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            {/* Header popup */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#333' }}>Escolher Projeto</h3>
              <button onClick={() => setPopupProducaoAberto(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999' }}>✕</button>
            </div>

            {/* Filtro */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <input
                type="text"
                placeholder="Buscar por ID, título ou cliente..."
                value={filtroProducao}
                onChange={e => setFiltroProducao(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            {/* Lista de projetos */}
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
              {producoesFiltradas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>Nenhum projeto encontrado</div>
              ) : (
                producoesFiltradas.map(p => {
                  const id = p.ProducaoId || p.producaoId;
                  const titulo = p.Titulo || p.titulo || 'Sem título';
                  const cliente = p.ClienteNome || p.clienteNome || '—';
                  const valor = p.OrcamentoValor || p.orcamentoValor || 0;
                  return (
                    <div
                      key={id}
                      onClick={() => selecionarProducao(p)}
                      style={{
                        padding: '10px 20px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                        display: 'grid', gridTemplateColumns: '60px 1fr 1fr 120px', gap: 10, alignItems: 'center',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: 700, color: '#1565c0', fontSize: 13 }}>#{id}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titulo}</div>
                      <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#2e7d32', textAlign: 'right' }}>{formatBRL(valor)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
